import {
    extractAccuracyTrustStrip,
    formatConfidenceBandLabel,
    formatCoveragePercent,
    trustStripToneForBand,
} from './accuracyModeMeta';
import { extractGroundingFields } from './intelligenceMeta';

/** Figma-style short band label (High / Medium / Low). */
export function formatBandPillLabel(band) {
    if (band == null || band === '') return null;
    const key = String(band).toLowerCase().trim();
    if (key === 'high') return 'High';
    if (key === 'medium' || key === 'moderate') return 'Medium';
    if (key === 'low') return 'Low';
    const long = formatConfidenceBandLabel(band);
    if (long === 'Strong Evidence') return 'High';
    if (long === 'Moderate Evidence') return 'Medium';
    if (long === 'Weak Evidence') return 'Low';
    return long || String(band);
}

/**
 * Evidence coverage labels for Ask Lens trust footer.
 * Strong evidence · Partial evidence · Limited evidence · No supporting evidence
 */
export function evidenceCoverageBandFromPercent(pct) {
    const n = Number(pct);
    if (!Number.isFinite(n)) return null;
    const value = n <= 1 ? n * 100 : n;
    if (value <= 0) return { label: 'No supporting evidence', tone: 'danger' };
    if (value >= 90) return { label: 'Strong evidence', tone: 'success' };
    if (value >= 70) return { label: 'Partial evidence', tone: 'warning' };
    return { label: 'Limited evidence', tone: 'danger' };
}

function parseCoveragePercentNumber(coveragePct) {
    if (coveragePct == null || coveragePct === '') return null;
    const n = Number(coveragePct);
    if (Number.isFinite(n)) {
        return n <= 1 ? Math.round(n * 100) : Math.round(n);
    }
    const fromString = String(coveragePct).match(/(\d+)/);
    if (fromString) return Number(fromString[1]);
    return null;
}

/** Ask Lens evidence coverage label (no percent prefix). */
export function formatEvidenceCoverageLabel(coveragePct) {
    const pctValue = parseCoveragePercentNumber(coveragePct);
    if (pctValue == null) return typeof coveragePct === 'string' ? coveragePct : null;
    return evidenceCoverageBandFromPercent(pctValue)?.label || null;
}

export function evidenceCoverageToneFromPercent(coveragePct) {
    const pctValue = parseCoveragePercentNumber(coveragePct);
    if (pctValue == null) return 'neutral';
    return evidenceCoverageBandFromPercent(pctValue)?.tone || 'neutral';
}

function pickActiveDecomposedConfidence(retrievalDebug) {
    const rd = retrievalDebug && typeof retrievalDebug === 'object' ? retrievalDebug : {};
    const decomposed = rd.coverage_report?.decomposed_confidence ?? rd.decomposed_confidence;
    if (!decomposed || typeof decomposed !== 'object' || !decomposed.active) return null;
    return decomposed;
}

/**
 * Lens Intelligence Confidence pill — synthesis band (answer grounding) when available.
 * Avoids decomposed overall.min, which is often capped at Moderate by the evidence axis
 * even when citation coverage is 100% (Evidence Coverage pill covers source mix separately).
 */
export function resolveLensConfidenceBand({ retrievalDebug, topLevelConfidence }) {
    const decomposed = pickActiveDecomposedConfidence(retrievalDebug);
    if (decomposed) {
        const synthesisBand = decomposed.synthesis?.band;
        if (synthesisBand) return String(synthesisBand).toLowerCase();
        const overallBand = decomposed.overall?.band;
        if (overallBand) return String(overallBand).toLowerCase();
    }
    if (topLevelConfidence != null && topLevelConfidence !== '') {
        return String(topLevelConfidence).toLowerCase();
    }
    return null;
}

const GENERIC_GOVERNANCE_CODES = new Set([
    'unsupported_claims_present',
    'orphan_ref_stripped',
    'orphan_citation_removed',
    'empty_citations_after_filter',
    'below_min_coverage',
    'low_fusion_no_refs',
    'blocked_weak_evidence_strict',
    'conservative_language_applied',
]);

const WARNING_CLAIM_STATUSES = new Set(['unsupported', 'inferred']);

const GAP_LANGUAGE =
    /\b(not (directly )?evidenced|could not be verified|insufficient evidence|no direct evidence|unclear from|not documented|missing evidence|requires further validation)\b/i;

const MAX_CLAIM_WARNINGS = 5;

function isSnakeCaseCode(text) {
    return /^[a-z][a-z0-9_]+$/.test(String(text || '').trim());
}

function normalizeWarningText(value) {
    const raw =
        typeof value === 'string'
            ? value
            : value?.message || value?.text || value?.description || value?.claim || value?.title;
    return String(raw || '').trim();
}

function shouldSkipGovernanceWarning(text) {
    const t = normalizeWarningText(text);
    if (!t) return true;
    if (GENERIC_GOVERNANCE_CODES.has(t)) return true;
    return isSnakeCaseCode(t);
}

function pushWarning(items, seen, value, { allowSnakeCase = false } = {}) {
    const t = normalizeWarningText(value);
    if (!t || seen.has(t)) return;
    if (!allowSnakeCase && shouldSkipGovernanceWarning(t)) return;
    seen.add(t);
    items.push(t);
}

function collectClaimGroundingLists(retrievalDebug) {
    const rd = retrievalDebug && typeof retrievalDebug === 'object' ? retrievalDebug : {};
    const synthesis = rd.synthesis_debug && typeof rd.synthesis_debug === 'object' ? rd.synthesis_debug : {};
    const lists = [
        rd.claim_grounding_sample,
        rd.claim_grounding,
        synthesis.claim_grounding_sample,
        synthesis.claim_grounding,
    ];
    return lists.filter(Array.isArray);
}

function hasCitationRefs(text) {
    return /\[\d+\]/.test(String(text || ''));
}

function isActionableClaimWarning(claim) {
    const status = String(claim?.status || '').toLowerCase();
    if (!WARNING_CLAIM_STATUSES.has(status)) return false;

    const text = normalizeWarningText(claim);
    if (!text) return false;

    const refs = Array.isArray(claim?.refs) ? claim.refs : [];
    if (refs.length > 0 || hasCitationRefs(text)) return false;

    if (GAP_LANGUAGE.test(text)) return true;
    if (status === 'inferred') return text.length <= 160;
    return text.length <= 180;
}

function extractUnsupportedClaimTexts(retrievalDebug) {
    const texts = [];
    const seen = new Set();
    for (const list of collectClaimGroundingLists(retrievalDebug)) {
        for (const claim of list) {
            if (!isActionableClaimWarning(claim)) continue;
            const text = normalizeWarningText(claim);
            if (!text || seen.has(text)) continue;
            seen.add(text);
            texts.push(text);
            if (texts.length >= MAX_CLAIM_WARNINGS) return texts;
        }
    }
    return texts;
}

/**
 * Confidence + evidence coverage badges for Lens Intelligence (Figma pills).
 * Uses the same extractAccuracyTrustStrip source as AI Warden AccuracyTrustStrip.
 */
export function extractLensIntelligenceBadges({ retrievalDebug, topLevelConfidence, isVerified }) {
    const strip = extractAccuracyTrustStrip({
        retrievalDebug,
        topLevelConfidence,
    });

    if (!strip && topLevelConfidence) {
        return {
            pipelineActive: true,
            inactiveReason: null,
            confidence: formatBandPillLabel(topLevelConfidence),
            confidenceTone: trustStripToneForBand(topLevelConfidence),
            evidenceCoverage: null,
            evidenceCoverageTone: null,
        };
    }

    if (strip?.pipelineActive === false) {
        return {
            pipelineActive: false,
            inactiveReason: strip.items?.[0]?.detail || strip.items?.[0]?.value || null,
            confidence: null,
            evidenceCoverage: null,
        };
    }

    const coverageItem = strip?.items?.find(i => i.key === 'coverage');

    const confidenceBand = resolveLensConfidenceBand({ retrievalDebug, topLevelConfidence });
    const confidence = confidenceBand ? formatBandPillLabel(confidenceBand) : null;

    let evidenceCoverage = null;
    let evidenceCoverageTone = null;
    // Ask Lens / verified answers always surface evidence coverage when available.
    if (coverageItem?.value != null && coverageItem.value !== '') {
        evidenceCoverage = formatEvidenceCoverageLabel(coverageItem.value);
        evidenceCoverageTone = evidenceCoverageToneFromPercent(coverageItem.value);
    } else if (isVerified === false) {
        evidenceCoverage = null;
        evidenceCoverageTone = null;
    } else {
        // Fall back: no coverage metric → no supporting evidence when empty sources.
        evidenceCoverage = null;
        evidenceCoverageTone = null;
    }

    return {
        pipelineActive: true,
        inactiveReason: null,
        confidence,
        confidenceTone: trustStripToneForBand(confidenceBand),
        evidenceCoverage,
        evidenceCoverageTone,
    };
}

/**
 * Validation warnings — alignment gaps, unsupported claim sentences, and governance messages.
 */
export function extractLensValidationWarnings({
    evidenceGovernance,
    retrievalDebug,
    alignmentAnalysis,
}) {
    const grounding = extractGroundingFields(evidenceGovernance, retrievalDebug);
    const items = [];
    const seen = new Set();

    const rd = retrievalDebug && typeof retrievalDebug === 'object' ? retrievalDebug : {};
    const gov = evidenceGovernance && typeof evidenceGovernance === 'object' ? evidenceGovernance : {};

    for (const g of alignmentAnalysis?.gaps || []) {
        pushWarning(items, seen, g);
    }

    for (const list of [alignmentAnalysis?.validation_errors, rd.alignment_validation_errors]) {
        if (!Array.isArray(list)) continue;
        for (const entry of list) {
            pushWarning(items, seen, entry);
        }
    }

    for (const text of extractUnsupportedClaimTexts(rd)) {
        pushWarning(items, seen, text);
    }

    const claimLists = [
        rd.unsupported_claims,
        gov.unsupported_claims,
        rd.validation_warnings,
        gov.validation_warnings,
        rd.claim_validation_warnings,
        gov.claim_validation_warnings,
    ];

    for (const list of claimLists) {
        if (!Array.isArray(list)) continue;
        for (const entry of list) {
            pushWarning(items, seen, entry);
        }
    }

    for (const w of grounding.warnings || []) {
        pushWarning(items, seen, w);
    }

    const unsupported = Number(grounding.unsupported_claim_count) || 0;
    if (unsupported > 0 && items.length === 0) {
        pushWarning(
            items,
            seen,
            `${unsupported} unsupported claim${unsupported === 1 ? '' : 's'} in the answer`,
            { allowSnakeCase: true },
        );
    }

    if (grounding.hallucination_risk_flag) {
        pushWarning(items, seen, 'Elevated narrative integrity risk — verify citations before relying on the answer');
    }

    void formatCoveragePercent(
        grounding.citation_coverage ??
            rd.citation_coverage ??
            rd.coverage_report?.citation_coverage,
    );

    return items;
}
